import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLoginModal } from "@/context/LoginModalContext";
import { useSettings } from "@/context/SettingsContext";
import { apiError } from "@/lib/api";
import { BRAND_LOGO } from "@/components/SEO";

export default function LoginModal() {
  const { requestOtp, verifyOtp } = useAuth();
  const { open, setOpen } = useLoginModal();
  const settings = useSettings();
  const nav = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const reset = () => { setStep("email"); setEmail(""); setOtp(""); setLoading(false); setCooldown(0); };
  const onOpenChange = (v) => { setOpen(v); if (!v) reset(); };

  const input = "w-full rounded-2xl border border-gray-200 px-4 py-3.5 text-navy outline-none focus:border-brandorange";

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) { toast.error("Enter a valid email address"); return; }
    setLoading(true);
    try {
      const data = await requestOtp(email);
      setStep("otp");
      setCooldown(30);
      if (data.dev_otp) toast.success(`OTP (demo): ${data.dev_otp}`, { duration: 9000 });
      else toast.success("OTP sent to your email — check your inbox");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await verifyOtp(email, otp);
      toast.success(`Welcome, ${user.name}!`);
      onOpenChange(false);
      nav(["admin", "staff"].includes(user.role) ? "/admin" : "/account");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="login-modal" className="max-w-md rounded-3xl p-8">
        <DialogTitle className="sr-only">Login with OTP</DialogTitle>
        <img src={settings?.logo || BRAND_LOGO} alt="Happy Holidays Wale" className="mx-auto mb-6 h-12 w-auto" />
        <h1 className="text-center font-display text-3xl font-bold text-navy">Login with OTP</h1>
        <p className="mt-1 text-center text-sm text-gray-500">A one-time code will be emailed to your registered email</p>
        {step === "email" ? (
          <form onSubmit={sendOtp} className="mt-7 space-y-4" data-testid="otp-phone-form">
            <div className="flex items-center gap-2 rounded-2xl border border-gray-200 px-3 focus-within:border-brandorange">
              <Mail className="h-5 w-5 text-brandorange" />
              <input data-testid="otp-phone" type="email" className="w-full py-3.5 text-navy outline-none" placeholder="Registered email address" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <button data-testid="otp-send" disabled={loading} className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold disabled:opacity-70">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send OTP"}</button>
          </form>
        ) : (
          <form onSubmit={doVerify} className="mt-7 space-y-4" data-testid="otp-verify-form">
            <p className="text-center text-sm text-gray-500">Enter the 6-digit code sent to <b>{email}</b></p>
            <input data-testid="otp-code" className={`${input} text-center text-2xl tracking-[0.5em]`} placeholder="______" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
            <button data-testid="otp-verify" disabled={loading} className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold disabled:opacity-70">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Verify & Login</>}</button>
            <button type="button" onClick={sendOtp} disabled={cooldown > 0 || loading} data-testid="otp-resend" className="w-full text-center text-sm font-semibold text-brandorange disabled:text-gray-400">{cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}</button>
            <button type="button" onClick={() => setStep("email")} className="w-full text-center text-sm font-semibold text-gray-400 hover:text-navy">← Change email</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
